import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { supabase as cloudSupabase } from './client';

// External Supabase project credentials (publishable/anon - safe to embed in frontend)
const EXTERNAL_SUPABASE_URL = 'https://qcqqmomgidxpqsxudovo.supabase.co';
const EXTERNAL_SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFjcXFtb21naWR4cHFzeHVkb3ZvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzEyNDkzNzcsImV4cCI6MjA4NjgyNTM3N30.qYLZRsOeupyvJA6c0tOBwWrBfQqicLNvqAobmodYa5g';

/**
 * Driver-specific Supabase client.
 * 
 * Unlike the general external-client (which routes auth to Cloud),
 * this client routes EVERYTHING to the external Supabase project
 * EXCEPT `functions` which stays on Lovable Cloud.
 * 
 * This ensures auth.uid() resolves correctly on the external DB
 * for RLS policies, Realtime subscriptions, and direct queries.
 */
const externalDriverClient = createClient<Database>(
  EXTERNAL_SUPABASE_URL,
  EXTERNAL_SUPABASE_ANON_KEY,
  {
    auth: {
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      persistSession: true,
      autoRefreshToken: true,
      storageKey: 'driver-auth-token',
    },
  }
);

/**
 * Driver client export:
 * - `.auth`, `.from()`, `.storage`, `.channel()`, `.removeChannel()`, `.rpc()` → external DB
 * - `.functions` → Lovable Cloud (edge functions are hosted there)
 */
export const driverSupabase = new Proxy(externalDriverClient, {
  get(target, prop, receiver) {
    // Only route functions to Cloud — everything else stays on external
    if (prop === 'functions') {
      return (cloudSupabase as any)[prop];
    }
    return Reflect.get(target, prop, receiver);
  },
}) as SupabaseClient<Database>;
